import axios from 'axios';
import { decryptToken } from '../utils/tokenUtils';
import AuthService from '../Services/auth/AuthService';
import { hashHref } from '../utils/appPaths';

// Must point at Laravel API root including `/api` (e.g. http://127.0.0.1:8000/api)
const baseURL =
  process.env.REACT_APP_BACKEND_URL || 'https://erp-b.redbackstage.top/api';

if (!process.env.REACT_APP_BACKEND_URL && typeof console !== 'undefined') {
  // eslint-disable-next-line no-console
  console.warn(
    '[axios] REACT_APP_BACKEND_URL is missing; using default',
    baseURL
  );
}

const axiosInstance = axios.create({
  baseURL,
});

axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      const decryptedToken = decryptToken(token.replace(/^encrypted:/, ''));
      if (decryptedToken) {
        config.headers.Authorization = `Bearer ${decryptedToken}`;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Paths where 401 means "wrong credentials" or "not logged in yet" — never hard-redirect
const AUTH_ENDPOINTS = [
  '/login',
  '/loginOtp',
  '/register',
  '/password',
  '/logout',
  '/reset-password-request',
  '/reset-password-submit',
];

function isAuthRequest(config) {
  const url = (config?.url || '').split('?')[0];
  return AUTH_ENDPOINTS.some((path) => url === path || url.endsWith(path));
}

axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    if (error.response && error.response.status === 401) {
      const config = error.config;

      // Wrong password / OTP, registration errors, etc. — let the UI handle the message
      if (isAuthRequest(config)) {
        return Promise.reject(error);
      }

      if (AuthService.hasPendingOtp()) {
        return Promise.reject(error);
      }

      const isLoggedIn = AuthService.isAuthenticated();
      if (isLoggedIn) {
        // Session expired or invalid token — clear and go to login once
        await AuthService.logout({ revokeOnServer: false });
        window.location.href = hashHref('/login');
      } else {
        // No token: 401 is not a "session expired" case; avoid reload loops
        localStorage.removeItem('authToken');
      }
    }
    return Promise.reject(error);
  }
);

export default axiosInstance;
